import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Linkedin, CheckCircle2, Loader2, ArrowRight, Calendar } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import BookDemoButton from '../components/BookDemoButton';

const V2Contact = () => {
  const [formData, setFormData] = useState({ name:'', email:'', company:'', phone:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch('https://formspree.io/f/xykblkyz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (r.ok) {
        setSubmitted(true);
        toast.success("Message sent — we'll be in touch shortly.");
        setFormData({ name:'', email:'', company:'', phone:'', message:'' });
      } else throw new Error();
    } catch {
      toast.error('Submission failed — please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="v2-contact">
      <Toaster position="top-right" richColors />

      <section className="pt-20 pb-12 md:pt-28 md:pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Contact</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
            Let&apos;s talk delivery.
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
            Book a demo, scope a deployment, or ask anything about governance, assurance and the Videra platform.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">Direct contact</p>
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 ring-4 ring-white shadow-md bg-slate-100">
                  <img
                    src="/images/team/peter-fulford.jpg"
                    alt="Peter Fulford — Founder & CEO"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div>
                  <p className="text-2xl font-medium text-slate-900">Peter Fulford</p>
                  <p className="text-sm text-slate-500 mt-1">Founder &amp; CEO · Videra · Keystone Strategic Consultants</p>
                </div>
              </div>
              <div className="mt-8 space-y-4">
                <a
                  href="mailto:hello@keystoneconsulting.uk"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-slate-700 hover:text-slate-900 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded flex items-center justify-center"><Mail className="w-4 h-4" /></div>
                  <span className="text-sm">hello@keystoneconsulting.uk</span>
                </a>
                <a
                  href="tel:+447703577261"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 text-slate-700 hover:text-slate-900 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded flex items-center justify-center"><Phone className="w-4 h-4" /></div>
                  <span className="text-sm">+44 (0) 7703 577 261</span>
                </a>
                <a
                  href="https://www.linkedin.com/in/peter-fulford-aa03467/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="flex items-center gap-4 text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded flex items-center justify-center"><Linkedin className="w-4 h-4" /></div>
                  <span className="text-sm">Connect on LinkedIn</span>
                </a>
              </div>
            </div>

            <div className="mt-6 bg-slate-900 text-white rounded-2xl p-8" data-testid="v2-contact-demo-card">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Book a demo</p>
              <h3 className="text-2xl font-medium mb-3">30 minutes to see Videra on your portfolio</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                We&apos;ll walk through the stage-gated lifecycle, AI insights, evidence packs and how Videra
                integrates with the systems you already use.
              </p>
              <BookDemoButton
                testid="v2-contact-book-demo"
                className="inline-flex items-center mt-6 bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors group cursor-pointer"
              >
                <Calendar className="mr-2 w-4 h-4" /> Book a demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </BookDemoButton>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-12 text-center" data-testid="v2-contact-success">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-medium text-slate-900">Thank you</h3>
                <p className="mt-4 text-slate-600">Your message has been received — we&apos;ll be in touch within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="mt-8 text-slate-900 font-medium hover:text-blue-600 transition-colors text-sm">Send another message</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5 bg-white border border-slate-200 rounded-2xl p-8" data-testid="v2-contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                    <input required name="name" value={formData.name} onChange={onChange} placeholder="Your name"
                      className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input required type="email" name="email" value={formData.email} onChange={onChange} placeholder="you@org.com"
                      className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Organisation</label>
                    <input name="company" value={formData.company} onChange={onChange} placeholder="Trust, FM provider, contractor"
                      className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                    <input name="phone" value={formData.phone} onChange={onChange} placeholder="+44…"
                      className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <textarea required name="message" rows={6} value={formData.message} onChange={onChange} placeholder="Tell us about your portfolio and what you're trying to solve…"
                    className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors resize-none" />
                </div>
                <button type="submit" disabled={submitting}
                  className="w-full bg-slate-900 text-white py-4 font-medium tracking-wide rounded-md hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send message'}
                </button>
                <p className="text-xs text-slate-500 text-center">We typically respond within 24 hours.</p>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default V2Contact;
